import { Dispatch, SetStateAction } from 'react'

import useSWR from 'swr'

import { useCallMutation } from '@/common/api/client'
import {
  apiDefaultFetcher,
  apiDeleteFetcher,
  apiPostFetcher,
  apiPutFetcher
} from '@/common/api/fetcher'
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/common/api/pagination'
import {
  DeleteAllW2MasterResponse,
  DeleteAllW2MasterVars,
  DeleteW2MasterRecordResponse,
  DeleteW2MasterRecordVars,
  GetW2MasterRecordResponse,
  GetW2MasterRecordVars,
  ListW2MasterResponse,
  ListW2MasterVars,
  UpdateW2MasterResponse,
  UpdateW2MasterVars
} from '@/common/api/params/w2Master'
import { ApiPath } from '@/common/api/path'
import { SearchConditionBase, SearchConditionParam, useSearchCondition } from '@/common/api/search'
import { MasterType } from '@/common/model/master/w2Master'

export const useMasterUpdateApi = (options?: {
  onSuccess?: (response: UpdateW2MasterResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<UpdateW2MasterResponse, UpdateW2MasterVars>(
    ApiPath.UPDATE_W2_MASTER,
    { ...options },
    apiPutFetcher
  )
}

export const useMasterDeleteAllApi = (options?: {
  onSuccess?: (response: DeleteAllW2MasterResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<DeleteAllW2MasterResponse, DeleteAllW2MasterVars>(
    ApiPath.DELETE_ALL_W2_MASTER,
    {
      ...options
    },
    apiDeleteFetcher
  )
}

export const useMasterDeleteApi = (options?: {
  onSuccess?: (response: DeleteW2MasterRecordResponse) => void
  onError?: (error: Response) => void
}) => {
  return useCallMutation<DeleteW2MasterRecordResponse, DeleteW2MasterRecordVars>(
    ApiPath.DELETE_W2_MASTER_RECORD,
    {
      ...options
    },
    apiDeleteFetcher
  )
}

interface MasterSearchListVars {
  site_id: number
  page?: number
  per_page?: number
  search?: string
  sort_by?: string
  sort_order?: string
}

export const convertConditionToMasterSearchListVars = (
  siteId: number,
  condition: MasterSearchCondition
): MasterSearchListVars => {
  return {
    site_id: siteId,
    page: condition.page,
    per_page: condition.perPage,
    ...(condition.keyword && {
      search: condition.keyword
    }),
    ...(condition.orderBy && {
      sort_by: condition.orderBy.split('-')[0],
      sort_order: condition.orderBy.split('-')[1]
    })
  }
}

export const useW2MasterGetApi = (vars: GetW2MasterRecordVars) => {
  return useSWR<GetW2MasterRecordResponse, Response, [string, GetW2MasterRecordVars]>(
    [ApiPath.GET_W2_MASTER_RECORD, vars],
    apiDefaultFetcher,
    { revalidateOnMount: true }
  )
}

export const useMasterSearchApi = (
  siteId: number,
  masterType: MasterType,
  condition: MasterSearchCondition
) => {
  return useSWR<ListW2MasterResponse, Response, [string, ListW2MasterVars]>(
    [
      ApiPath.LIST_W2_MASTER,
      {
        ...convertConditionToMasterSearchListVars(siteId, condition),
        master_type: masterType
      }
    ],
    apiDefaultFetcher
  )
}

export const MasterOrderBy = {
  ID_ASC: 'id-asc',
  ID_DESC: 'id-desc',
  CREATED_AT_ASC: 'created-at-asc',
  CREATED_AT_DESC: 'created-at-desc',
  UPDATED_AT_ASC: 'updated-at-asc',
  UPDATED_AT_DESC: 'updated-at-desc'
} as const

export type MasterOrderBy = (typeof MasterOrderBy)[keyof typeof MasterOrderBy]

export const useMasterSearchCondition = (): [
  MasterSearchCondition,
  Dispatch<SetStateAction<MasterSearchCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<MasterSearchCondition>(masterSearchConditionParam)
}

export interface MasterSearchCondition extends SearchConditionBase {
  keyword: string | null
  orderBy: MasterOrderBy | null
}

const masterSearchConditionParam: SearchConditionParam[] = [
  {
    name: 'keyword',
    type: 'string'
  },
  {
    name: 'orderBy',
    type: 'string'
  }
]

export const MASTER_SEARCH_CONDITION_CLEAR: MasterSearchCondition = {
  page: DEFAULT_PAGE,
  perPage: DEFAULT_PER_PAGE,
  keyword: null,
  orderBy: null
}
